import React, { useContext, useEffect, useCallback } from "react";

import { SurveyContext } from "../../providers/survey";
import { SurveyRespondentDataContext } from "../../providers/survey-respondent-data";
import { SurveySplashDataContext } from "../../providers/survey-splash-data";
import { SurveyValidInputContext } from "../../providers/survey-valid-input";
import { SurveyNavigationContext } from "../../providers/survey-navigation";

import SurveyDimension from "../survey-dimension";

import useViewSize from "../../hooks/use-view-size";

import CustomToast from "../custom-toast";
import CustomButton from "../custom-button";
import useCompatNavigate from "../../hooks/use-compat-navigate";

const Survey = () => {
  const { width } = useViewSize();
  const { questionnaire, pages, answers, saveAnswers, finishSurvey } = useContext(SurveyContext);
  const { wasValidated, updateValidInput, isAllInputValid, resetValidInput } =
    useContext(SurveyValidInputContext);
  const { currentPage, navigateToPreviousPage, navigateToNextPage } =
    useContext(SurveyNavigationContext);

  const { isAnonymousSurvey } = useContext(SurveyRespondentDataContext);
  const { surveyMetadata } = useContext(SurveySplashDataContext);
  const { surveyRoute } = surveyMetadata;

  const compatNavigate = useCompatNavigate();

  const isLastPage = currentPage === Object.keys(pages).length;

  // El sistema verificará previamente si el usuario tiene respuestas guardadas de una sesión anterior
  // De ser así, mostrará este mensaje en pantalla
  useEffect(() => {
    if (Object.keys(answers).length > 0) {
      CustomToast.info({
        messageTitle: "Bienvenido de nuevo!",
        Body: (
          <>
            <h6 className="text-justify">
              Hemos cargado el formulario desde donde terminaste la última vez.
            </h6>
            <h6 className="mb-2 text-justify">
              Puedes navegar por las páginas si deseas modificar alguna de tus respuestas.
            </h6>
          </>
        ),
        showDismissButton: true,
      });
    }
  }, []);

  // Efecto que se acciona al cambiar de página, nos devuelve al inicio de la página
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  // Se ejecuta al pasar con éxito la validación de la página completa, es decir, al no hallar preguntas sin responder
  const onPassedValidation = useCallback(async () => {
    // Si es la última página del cuestionario, guarda las respuestas y finaliza el cuestionario (cambia el status a 'done').
    if (isLastPage) {
      await saveAnswers();
      await finishSurvey();
      compatNavigate(`/encuestas/${surveyRoute}/formulario/exito`);
      return;
    }

    // De aquí en adelante aplica a casos donde el usuario aún no llega al final del cuestionario.

    // Para encuestas con identificador, se guardan las respuestas parciales en la base de datos remota
    if (!isAnonymousSurvey) {
      await saveAnswers();
      CustomToast.success({
        messageTitle: "Tus respuestas se han guardado de manera exitosa",
      });
    }

    // Finalmente pasamos a la siguiente página y se reinicia el input válido de la página, para que no aparezca en las nuevas preguntas
    navigateToNextPage();
    resetValidInput();
  }, [isLastPage, answers]);

  // Efecto que se acciona solamente cuando el usuario presionó Enviar o Siguiente (se ejecutó la validación de sus respuestas) y sus respuestas son todas válidas.
  useEffect(() => {
    if (wasValidated && isAllInputValid) {
      onPassedValidation();
    }
  }, [wasValidated, isAllInputValid, onPassedValidation]);

  const onClickPreviousPage = () => {
    navigateToPreviousPage();
    resetValidInput();
  };

  // Cuando el usuario presiona "Siguiente" o "Enviar", se ejecutará la función updateValidInput(), que verificará que todas las preguntas en la página actual tienen una respuesta considerada como válida
  // El paso a la siguiente página del formulario será como consecuencia del resultado de esta función. El effect que se encarga de eso se puede ver más arriba
  const onClickNextPage = () => {
    updateValidInput();
  };
  const onSubmit = (event) => {
    event.preventDefault();
    updateValidInput();
  };

  return (
    <>
      <h2
        className={`font-weight-bold text-primary mb-4 animated--fade-in text-uppercase ${
          width <= 768 ? "text-center mt-4" : ""
        }`}
      >
        {surveyMetadata.name}
      </h2>
      <form noValidate onSubmit={onSubmit}>
        {pages[currentPage].map((dimension) => {
          const dimensionIndex = dimension - 1;
          return (
            <SurveyDimension
              key={`survey-dimension-${dimensionIndex}`}
              dimensionIndex={dimensionIndex}
              dimensionName={questionnaire[dimensionIndex]["dimension_name"]}
              dimensionDescription={questionnaire[dimensionIndex]["dimension_description_data"]}
              singleRowQuestions={questionnaire[dimensionIndex]["single_row_questions"]}
              questions={questionnaire[dimensionIndex]["questions"]}
              optional={questionnaire[dimensionIndex]["optional"]}
              showOptionalMessage={questionnaire[dimensionIndex]["show_optional_message"]}
              clearable={questionnaire[dimensionIndex]["clearable"]}
            />
          );
        })}
        <div className={`text-right pt-2 ${width <= 768 ? "mr-2" : ""}`}>
          {currentPage > 1 && (
            <CustomButton
              onClick={onClickPreviousPage}
              text="Anterior"
              icon="chevron-left"
              size="lg"
            />
          )}
          {isLastPage ? (
            <CustomButton
              type="submit"
              text="Enviar"
              color="yellow"
              icon="location-arrow"
              size="lg"
              additionalClasses="ml-2"
            />
          ) : (
            <CustomButton
              onClick={onClickNextPage}
              text="Siguiente"
              icon="chevron-right"
              size="lg"
              additionalClasses="ml-2"
            />
          )}
        </div>
      </form>
    </>
  );
};

export default Survey;
